import api from '@/services/api'
import { camelToSnake, snakeToCamel } from '../../../utils/object'

import Application from '@/services/applications'

export default class UserAPI {
  static USERS_ENDPOINT = 'users'
  static REGISTRATION_ENDPOINT = 'signup'
  static LOGIN_ENDPOINT = 'login'
  static BULK_UPDATE_STATUS_ENDPOINT = 'users/bulk-update-status/'
  static DRY_RUN_UPLOAD_EVALUATORS_ENDPOINT = 'users/dry-run-upload-evaluators/'
  static UPLOAD_EVALUATORS_ENDPOINT = 'users/upload-evaluators/'

  /**
   * Initialize a `UserAPI` instance.
   *
   * @param {class} cls  The class to use to create user objects.
   **/
  constructor(cls) {
    this.cls = cls
  }

  /**
   * Factory method to create a new instance of `UserAPI`.
   *
   * @param {class} cls      The class to use to create user objects.
   **/
  static create(cls) {
    return new UserAPI(cls)
  }

  /**
   * Get the API Client.
   */
  get client() {
    return api.getApiClient()
  }

  /**
   * Retrieve one user from the API.
   *
   * @param {String} userID  Unique ID of the user to retrieve.
   */
  get(userID) {
    const url = `${UserAPI.USERS_ENDPOINT}/${userID}/`
    var responseData = this.client.get(url).then(response => this.cls.fromAPI(response.data))
    //console.log("responseData")
    //console.log(responseData)
    return responseData
  }

  /**
   * Create a User via the API.
   */
  create(user, fields = []) {
    const url = `${UserAPI.USERS_ENDPOINT}/`
    const data = this.cls.toAPI(user, fields)
    if (!data.date_joined) {
      data.date_joined = new Date().toISOString()
    }
    return this.client
      .post(url, data)
      .then(response => response.data)
      .then(this.cls.fromAPI)
  }

  /**
   * Get all users through the API.
   *
   *
   */
  list({ filters = {} } = {}) {
    const url = `${UserAPI.USERS_ENDPOINT}/`

    const options = {
      params: {},
    }

    // Filters
    if (filters.isEvaluator) {
      options.params.is_evaluator = filters.isEvaluator
    }

    if (filters.name) {
      options.params.name = filters.name
    }

    if (filters.preferences) {
      options.params.preferences = filters.preferences.toString()
    }

    if (filters.tags) {
      options.params.tags = filters.tags.toString()
    }

    if (filters.pageSize) {
      options.params.page_size = filters.pageSize
    }

    if (filters.page) {
      options.params.page = filters.page
    }

    if (filters.status) {
      options.params.status = filters.status
    }

    if (filters.assignedApplications) {
      options.params.assigned_applications = filters.assignedApplications ? 'True' : 'False'
    }

    if (filters.pendingAsEvaluator) {
      options.params.pending_as_evaluator = filters.pendingAsEvaluator
    }

    if (filters.rejectedAsEvaluator) {
      options.params.rejected_as_evaluator = filters.rejectedAsEvaluator
    }

    return this.client
      .get(url, options)
      .then(response => response.data)
      .then(data => {
        // To Do, add data.results instead of data when you add pagination
        data.results = data.results.map(this.cls.fromAPI)
        return data
      })
  }

  /**
   * Update a user through the API.
   *
   * @param {User} user  User data to send to the API.
   */
  update(user, fields = [], excludeFields = []) {
    const url = `${UserAPI.USERS_ENDPOINT}/${user.id}/`
    const data = this.cls.toAPI(user, fields, excludeFields)
    return this.client.patch(url, data).then(response => this.cls.fromAPI(response.data))
  }

  /**
   * Register a new user through the API.
   *
   * @param {Object} data   Object with the fields required for registration.
   */
  register(data) {
    return this.client
      .post(`${UserAPI.REGISTRATION_ENDPOINT}/`, camelToSnake(data))
      .then(response => this.cls.fromAPI(response.data))
      .catch(error => {
        // eslint-disable-next-line no-console
        console.log('Register API error')
        throw error
      })
  }

  /**
   * Log in a user through the API.
   *
   * @param {String} email
   * @param {String} password
   */
  login(email, password) {
    return this.client
      .post(`${UserAPI.LOGIN_ENDPOINT}/`, { email, password })
      .then(response => this.cls.fromAPI(response.data))
      .catch(error => {
        // eslint-disable-next-line no-console
        console.log('Login API error')
        throw error
      })
  }

  /**
   * Get all applications assigned to a User evaluator. Returns an application instance.
   *
   * @param {String} UserId  Unique ID of the application's evaluation to retrieve.
   */
  listApplications(userId) {
    const url = `${UserAPI.USERS_ENDPOINT}/${userId}/assignments/`
    const options = {
      params: {},
    }
    var dataToReturn =  this.client
      .get(url, options)
      .then(response => response.data)
      .then(data => {
        // To Do, add data.results instead of data when you add pagination
        data = data.map(Application.fromAPI)
        return data
      })
      console.log("listApplications-dataToReturn")
      console.log(dataToReturn)
      return dataToReturn
  }

  /**
   * Get all applications assigned to a User evaluator. Returns an application instance.
   *
   * @param {String} UserId  Unique ID of the application's evaluation to retrieve.
   */
  getAllEvaluators() {
    const url = `${UserAPI.USERS_ENDPOINT}/get-all-evaluators/`
    return this.client
      .get(url)
      .then(response => response.data)
      .then(data => {
        data = data.map(response => this.cls.fromAPI(response))
        return data
      })
  }

  /**
   * Bulk update the status of a set of evaluators.
   *
   * @param {List of String} evaluators - IDs of the evaluators to update.
   * @param {String} status - Status to set on all provided evaluators.
   */
  bulkUpdateStatus(evaluators, status) {
    const url = UserAPI.BULK_UPDATE_STATUS_ENDPOINT
    const data = {
      evaluators: evaluators,
      status: status,
    }
    return this.client
      .post(url, data)
      .then(response => response.data)
      .then(data => {
        data = data.map(e => this.cls.fromAPI(e))
        return data
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.log('Update Status API error')
        throw error
      })
  }

  /**
   * Do a dry run of uploading evaluators through a CSV.
   *
   * @param {File} file - CSV of evaluators to upload.
   */
  uploadEvaluatorsDryRun(file) {
    const url = UserAPI.DRY_RUN_UPLOAD_EVALUATORS_ENDPOINT
    let data = new FormData()
    data.append('file', file)

    return this.client
      .post(url, data)
      .then(response => snakeToCamel(response.data))
      .catch(error => {
        console.log('Upload Evaluators Dry Run API Error')
        throw error
      })
  }

  /**
   * Upload evaluators to the database through a CSV.
   *
   * @param {File} file - CSV of evaluators to upload.
   */
  uploadEvaluators(file) {
    const url = UserAPI.UPLOAD_EVALUATORS_ENDPOINT
    let data = new FormData()
    data.append('file', file)

    return this.client
      .post(url, data)
      .then(response => snakeToCamel(response.data))
      .catch(error => {
        console.log('Upload Evaluators API Error')
        throw error
      })
  }
}
